/**
 * Serviço unificado para cálculo de orçamento
 * 
 * Lógica:
 * 1. Se tem cliente: tenta desconto primeiro, se não tiver desconto → escalonamento
 * 2. Se não tem cliente: vai direto para escalonamento
 * 3. Prioridade: desconto > escalonamento > valor base
 */

import { checkDiscount, type CheckDiscountResponse } from './discountService';
import { calculateEscalonamento, type EscalonamentoResult } from './escalonamentoService';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import { LogCategory } from '../../../../config/logging';

export interface CalculateQuotationRequest {
  /** ID do cliente (opcional - se não fornecido, usa apenas escalonamento) */
  cli_id?: number | null;
  /** ID do produto */
  pro_id: number;
  /** ID da qualidade/subcategoria (opcional) */
  qualidade_id?: number | null;
  /** ID da categoria */
  categoria_id: number;
  /** Metragem ou quantidade (depende de produtoUnd) */
  metragem: number;
  /** Unidade do produto (3 = metros, 4 = unidade) */
  produtoUnd: number;
  /** Contexto (opcional, padrão: 'default') */
  contexto?: string;
}

export interface CalculateQuotationResult {
  /** Valor total calculado */
  valor_total: number;
  /** Preço unitário (por metro linear ou por unidade) */
  preco_unitario: number;
  /** Metragem/quantidade usada no cálculo */
  metragem: number;
  /** Fonte do cálculo: 'discount' | 'escalonamento' | 'base' */
  fonte: 'discount' | 'escalonamento' | 'base';
  /** Dados do desconto (se aplicado) */
  desconto?: {
    has_discount: boolean;
    discount_value: number | null;
    product_base_price: number;
    final_price: number;
  };
  /** Dados do escalonamento (se usado) */
  escalonamento?: EscalonamentoResult;
  /** Descrição do cálculo */
  descricao?: string;
  /** Faixa e nível (se disponível) */
  faixa?: string;
  nivel?: string;
}

/**
 * Calcula orçamento seguindo a lógica:
 * 1. Se tem cliente: tenta desconto → se não tiver → escalonamento
 * 2. Se não tem cliente: vai direto para escalonamento
 * 3. Prioridade: desconto > escalonamento > valor base
 */
export async function calculateQuotation(
  request: CalculateQuotationRequest
): Promise<CalculateQuotationResult> {
  const {
    cli_id,
    pro_id,
    qualidade_id,
    categoria_id,
    metragem,
    produtoUnd,
    contexto = 'default'
  } = request;

  // Validações básicas
  if (!pro_id || pro_id <= 0) {
    throw new Error('Produto inválido');
  }
  if (!categoria_id || categoria_id <= 0) {
    throw new Error('Categoria inválida');
  }
  if (!qualidade_id || qualidade_id <= 0) {
    throw new Error('Qualidade inválida');
  }
  if (!metragem || metragem <= 0) {
    throw new Error('Metragem inválida');
  }

  sendToTerminal('🔍 [QUOTATION] Iniciando cálculo de orçamento', {
    cli_id,
    pro_id,
    qualidade_id,
    categoria_id,
    metragem,
    produtoUnd
  }, LogCategory.BUSINESS);

  // ═══════════════════════════════════════════════════════════════════════════
  // CENÁRIO 1: Com cliente selecionado → Tenta desconto primeiro
  // ═══════════════════════════════════════════════════════════════════════════
  if (cli_id && cli_id > 0) {
    try {
      sendToTerminal('💰 [QUOTATION] Cliente fornecido, verificando desconto...', {
        cli_id,
        pro_id,
        qualidade_id
      }, LogCategory.BUSINESS);

      const discountResult = await checkDiscount({
        cli_id,
        pro_id,
        one1_outro_id: qualidade_id ?? undefined
      });

      // Se tem desconto válido, usar o final_price
      if (discountResult.has_discount && discountResult.final_price > 0) {
        const valorTotal = discountResult.final_price * metragem;
        const precoUnitario = discountResult.final_price;

        sendToTerminal('✅ [QUOTATION] Desconto aplicado com sucesso', {
          final_price: discountResult.final_price,
          product_base_price: discountResult.product_base_price,
          discount_value: discountResult.discount_value,
          metragem,
          valor_total: valorTotal
        }, LogCategory.BUSINESS);

        return {
          valor_total: valorTotal,
          preco_unitario: precoUnitario,
          metragem,
          fonte: 'discount',
          desconto: {
            has_discount: discountResult.has_discount,
            discount_value: discountResult.discount_value ?? null,
            product_base_price: discountResult.product_base_price,
            final_price: discountResult.final_price
          },
          descricao: `Desconto aplicado: R$ ${discountResult.product_base_price.toFixed(2)} → R$ ${discountResult.final_price.toFixed(2)} por metro linear`
        };
      } else {
        // Tem cliente mas não tem desconto → seguir para escalonamento
        sendToTerminal('ℹ️ [QUOTATION] Cliente sem desconto, usando escalonamento', {
          cli_id,
          has_discount: discountResult.has_discount,
          final_price: discountResult.final_price
        }, LogCategory.BUSINESS);
      }
    } catch (discountError) {
      // Erro ao verificar desconto → tratar como "sem desconto" e seguir para escalonamento
      const errorMessage = discountError instanceof Error ? discountError.message : String(discountError);
      sendToTerminal('⚠️ [QUOTATION] Erro ao verificar desconto, usando escalonamento', {
        error: errorMessage,
        cli_id
      }, LogCategory.ERROR);
      // Não lançar erro, apenas seguir para escalonamento
    }
  } else {
    sendToTerminal('ℹ️ [QUOTATION] Cliente não fornecido, usando escalonamento diretamente', {}, LogCategory.BUSINESS);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CENÁRIO 2: Sem desconto ou sem cliente → Usar escalonamento
  // ═══════════════════════════════════════════════════════════════════════════
  try {
    sendToTerminal('📊 [QUOTATION] Calculando escalonamento...', {
      pro_id,
      categoria_id,
      qualidade_id,
      metragem,
      produtoUnd
    }, LogCategory.BUSINESS);

    const escalonamentoResult = await calculateEscalonamento({
      proId: pro_id,
      categoriaId: categoria_id,
      qualidadeId: qualidade_id!,
      metragem,
      produtoUnd,
      contexto
    });

    const valorTotal = escalonamentoResult.valor_total ?? escalonamentoResult?.dados?.valor_total ?? 0;
    const precoUnitario = escalonamentoResult.preco_m2 ?? escalonamentoResult?.dados?.preco_m2 ?? 0;

    sendToTerminal('✅ [QUOTATION] Escalonamento calculado com sucesso', {
      valor_total: valorTotal,
      preco_unitario: precoUnitario,
      faixa: escalonamentoResult.faixa,
      nivel: escalonamentoResult.nivel
    }, LogCategory.BUSINESS);

    return {
      valor_total: valorTotal,
      preco_unitario: precoUnitario,
      metragem,
      fonte: 'escalonamento',
      escalonamento: escalonamentoResult,
      descricao: escalonamentoResult.descricao || escalonamentoResult?.dados?.descricao || 'Cálculo realizado com sucesso',
      faixa: escalonamentoResult.faixa || escalonamentoResult?.dados?.faixa,
      nivel: escalonamentoResult.nivel || escalonamentoResult?.dados?.nivel
    };
  } catch (escalonamentoError) {
    const errorMessage = escalonamentoError instanceof Error ? escalonamentoError.message : String(escalonamentoError);
    sendToTerminal('❌ [QUOTATION] Erro ao calcular escalonamento', {
      error: errorMessage
    }, LogCategory.ERROR);
    throw new Error(`Erro ao calcular orçamento: ${errorMessage}`);
  }
}

